/**
 * SupportReportButton (ROP-teams host) - the bottom-left lifebuoy every pilot
 * user can reach (Bill 2026-09-11). The button, dialog, background log capture
 * and screen picture are the shared @retail-ops/support-report package (also
 * in the Admin, Risk Management and SysOps panels, 2026-10-07); this file only
 * supplies what ROP-teams knows: who you are, live service status, and the
 * redacted debug snapshot (API log, error buffer, cart + dialog state).
 */
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { SupportReportButton as SharedSupportReportButton, type SupportEventWrite } from '@retail-ops/support-report';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useSystemStatus } from '@/hooks/useSystemStatus';
import { APP_VERSION, APP_BUILD_CHANNEL, APP_BUILD_TIME } from '@/constants/version';
import { useDebugSnapshot } from '@/components/layout/DebugPanel/hooks/useDebugSnapshot';
import { redactDebugPayload } from '@/components/layout/DebugPanel/utils/redactDebugPayload';
import { installApiCallLog } from '@/components/layout/DebugPanel/services/apiCallLogStore';
import { installErrorBuffer } from '@/components/layout/DebugPanel/services/errorBufferStore';

// The debug capture stores are installed by DebugPanel at ITS module load - which for a
// pilot user never happens (the panel is Test-Mode only). Install here too; both are idempotent.
installApiCallLog();
installErrorBuffer();

export function SupportReportButton(): JSX.Element {
  const { user } = useAuth();
  const client = useApiClient();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const status = useSystemStatus(open);
  const { build } = useDebugSnapshot();

  const reporter = useMemo(() => ({
    upn: user?.upn ?? null,
    displayName: user?.displayName ?? null,
    officerCode: user?.officerCode ?? null,
    role: user?.primaryRole ?? null,
    branch: user?.activeBranchCode ?? user?.branchCode ?? null,
  }), [user]);

  const statusMap = useMemo(() => ({
    'Core Director': String(status.coreDirector),
    LexisNexis: String(status.lexisNexis),
    USPS: String(status.usps),
    Scanner: String(status.scannerBridge),
  }), [status.coreDirector, status.lexisNexis, status.usps, status.scannerBridge]);

  const submit = useCallback((event: SupportEventWrite) => client.retail.events.write(event), [client]);
  const extraSnapshot = useCallback(() => redactDebugPayload(build()) as Record<string, unknown>, [build]);

  return (
    <SharedSupportReportButton
      appName="Retail Operations (Teams app)"
      appVersion={APP_VERSION}
      appChannel={APP_BUILD_CHANNEL}
      appBuild={APP_BUILD_TIME}
      reporter={reporter}
      page={loc.pathname + loc.search}
      status={statusMap}
      submit={submit}
      extraSnapshot={extraSnapshot}
      onOpenChange={setOpen}
    />
  );
}

export default SupportReportButton;
